import type { SeriesName } from "@root/constants";

import type { BedroomBlockType } from "../components/BedroomBlock/BedroomBlock.types";
import type { TopBlockType } from "../components/TopBlock/TopBlock";

export const SEASON_3_TECHNIQUE_PAGES = [
  "wrapping",
  "rippling",
  "streaming",
  "chilling",
  "grinding",
  "outstroking",
  "transferring",
  "compressing",
  "levering",
  "girthing",
  "mode-play",
  "concealing",
] as const;

export type Season3TechniquePage = (typeof SEASON_3_TECHNIQUE_PAGES)[number];

export const SEASON_2_TECHNIQUE_PAGES = [
  "shallowing",
  "pairing",
  "rocking",
  "broadening",
  "flexing",
  "g-regions",
  "angling",
  "renewing",
  "breathing",
  "booty",
  "adding",
  "squirting",
  "deep-end",
] as const;

export type Season2TechniquePage = (typeof SEASON_2_TECHNIQUE_PAGES)[number];

export const SEASON_1_TECHNIQUE_PAGES = [
  "framing",
  "signaling",
  "consistency",
  "rhythm",
  "layering",
  "hinting",
  "staging",
  "surprise",
  "accenting",
  "orbiting",
  "edging",
  "multiples",
] as const;

export const SERIES_PAGES_MAP: Record<
  SeriesName,
  ReadonlyArray<TechniquePage>
> = {
  series1: SEASON_1_TECHNIQUE_PAGES,
  series2: SEASON_2_TECHNIQUE_PAGES,
  series3: SEASON_3_TECHNIQUE_PAGES,
};

export type Season1TechniquePage = (typeof SEASON_1_TECHNIQUE_PAGES)[number];

export type TechniquePage =
  | Season1TechniquePage
  | Season2TechniquePage
  | Season3TechniquePage;

export enum TechniquePagesCommonSections {
  topBlock = "topBlock",
  topAnimation = "topAnimation",
  pageHeading = "pageHeading",
  quoteSliderBanner = "quoteSliderBanner",
  quoteBanner = "quoteBanner",
  greatestHitsBlock = "greatestHitsBlock",
  bedroomBlockCommon = "bedroomBlockCommon",
  techniquesBlock = "techniquesBlock",
}

export type PageHeadingFields = {
  icon: TechniquePage;
  subtitle: string;
  title: string;
};

export type PageHeadingType = {
  component: TechniquePagesCommonSections.pageHeading;
  fields: PageHeadingFields;
};

export type QuoteSliderBannerFields = {
  image: { path: string };
  heading: string;
  quotes: string[];
};

export type QuoteSliderBannerType = {
  component: TechniquePagesCommonSections.quoteSliderBanner;
  fields: QuoteSliderBannerFields;
};

export type Sections = Array<{
  component: TechniquePagesCommonSections;
  fields: Record<string, any>;
}>;

export type TechniquePageContent = {
  content: Sections;
};

export type TechniquePageSections = {
  heading: {
    [TechniquePagesCommonSections.pageHeading]: PageHeadingType;
    [TechniquePagesCommonSections.topBlock]: TopBlockType;
  };
  [TechniquePagesCommonSections.quoteSliderBanner]: QuoteSliderBannerType[];
  [TechniquePagesCommonSections.bedroomBlockCommon]: BedroomBlockType;
};

export type TechniqueSvgIconFields = {
  icon: string;
};

export type CopyBlockFields = {
  heading: string;
  copy: string;
  cssClass?: string;
};

export type CopyBlockProps = {
  heading?: string;
  copy?: string | string[];
  cssClass?: string;
};

export type StatBlockFields = {
  ratio: string;
  copy: string;
};

export type VideoFields = {
  video: {
    captionLocales: string;
    captionSource: string;
    coverImage: string;
    id: string;
    videoUrl: string;
    duration: string;
    explicit?: boolean;
  };
  subContent: { name: string; copy: string };
  explicit: {
    isExplicit: boolean;
    label?: string;
    overlayCopy?: string;
    letsGo?: string;
    okGotIt?: string;
  };
};

export type BasicVideoBlockFields = Omit<VideoFields, "subContent"> & {
  subContent: { participantName: string; caption: string };
};

export type AnimationVideoBlockFields = {
  video: {
    id?: string;
    srcName?: string;
    url: string;
    autoplay: boolean;
    loop: boolean;
    playerElement?: string;
    type?: string;
    muted?: boolean;
    playsInline?: boolean;
  };
};

export type AnimationGifBlockFields = {
  gifStill: string;
  gifMovie: string;
  links: {
    preview: { text: string };
    stop: { text: string };
  };
};

export type Season1VideoFields = {
  video: {
    explicit: boolean;
    id: string;
    videoId: string;
    coverImage: string;
    captionSource: string;
    label: string;
    captionLocales: string;
    participantName?: string;
  };
};

export const ALL_TECHNIQUE_PAGES: ReadonlyArray<TechniquePage> = [
  ...SEASON_1_TECHNIQUE_PAGES,
  ...SEASON_2_TECHNIQUE_PAGES,
  ...SEASON_3_TECHNIQUE_PAGES,
];

export const isTechniquePage = (id: string): id is TechniquePage =>
  ALL_TECHNIQUE_PAGES.includes(id as TechniquePage);

/**
 * URL slugs that reach us for a technique page but are not its id. The one
 * live case: Season 3's "Grinding on Toys" has page id `grinding`, and links
 * built from the title (Contentful `slug || kebabCase(title)`) emit
 * `grinding-on-toys`. Anything not in the three page lists used to fall
 * through to a dynamic import of a JSON file that does not exist and take the
 * page down (Sentry NMH-REACT-CZN, OMG-2593). Every route builder AND the
 * route itself go through `resolveTechniquePageId`, so an alias is both never
 * emitted and, if it arrives anyway (bookmark, old email), redirected.
 */
export const TECHNIQUE_PAGE_ALIASES: Readonly<Record<string, TechniquePage>> = {
  "grinding-on-toys": "grinding",
};

/**
 * The canonical page id for a slug, or `undefined` when it names no page.
 * Case-insensitive: `/techniques/Grinding` is the same page.
 */
export const resolveTechniquePageId = (
  slug: string | undefined | null,
): TechniquePage | undefined => {
  if (!slug) return undefined;
  const normalized = slug.trim().toLowerCase();
  const candidate = TECHNIQUE_PAGE_ALIASES[normalized] ?? normalized;
  return isTechniquePage(candidate) ? candidate : undefined;
};
